// Mandate breach monitoring: the Desk side of `wealth.compliance.mandate-breaches`
// (ADR-0027). A client in breach of their risk or of the allocation limits of
// their profile carries a flag in the client table of the Desk Dashboard, the
// chips above the table filter by the flag, and the tape counts the clients
// in breach.
import FilterChip from '@/components/ui/FilterChip';
import Pill from '@/components/ui/Pill';
import type { StatStripItem } from '@/components/ui/StatStrip';
import { defineWebFeature } from '@/features/kit';
import { ABSENT } from '@/lib/overview-figures';
import {
    clientBreachFlags,
    matchesFlagFilter,
    type ClientBreachFlags,
    type ClientFlagFilter,
} from '@/pages/desk/my-book/book-panels';
import type { ClientFlagSource, DeskDashboardCtx } from '@/pages/desk/my-book/slots';

const FILTERS: readonly string[] = ['flagged', 'overRisk', 'allocation'];

/** The breach flags of each client in view, from the breaches of the overview. */
function flagsInView(ctx: DeskDashboardCtx): Map<string, ClientBreachFlags> {
    const breaches = (ctx.overview?.breaches ?? []).filter((b) => ctx.keepClient(b.clientId));
    return clientBreachFlags(breaches);
}

/** The client filter of this source, or null when the filter is not one of its own. */
function ownFilter(filter: string | null): ClientFlagFilter {
    return filter !== null && FILTERS.includes(filter) ? (filter as ClientFlagFilter) : null;
}

function breachFlagSource(ctx: DeskDashboardCtx): ClientFlagSource {
    const flagsById = flagsInView(ctx);
    const filter = ownFilter(ctx.clientFilter);
    const count = (f: ClientFlagFilter) =>
        ctx.clients.filter((c) => matchesFlagFilter(flagsById.get(c.clientId), f)).length;
    return {
        badges: (clientId) => {
            const flags = flagsById.get(clientId);
            return (
                <>
                    {flags?.overRisk && (
                        <span title={flags.overRiskDetail.join('\n')}>
                            <Pill variant="red" label="Over risk" testId="client-flag-over-risk" />
                        </span>
                    )}
                    {flags?.allocation && (
                        <span title={flags.allocationDetail.join('\n')}>
                            <Pill
                                variant="amber"
                                label="Allocation"
                                testId="client-flag-allocation"
                            />
                        </span>
                    )}
                </>
            );
        },
        matches: (clientId, active) =>
            matchesFlagFilter(flagsById.get(clientId), ownFilter(active)),
        chips: (
            <>
                <FilterChip
                    label={`Over risk (${count('overRisk')})`}
                    isSelected={filter === 'overRisk'}
                    onClick={() => ctx.toggleClientFilter('overRisk')}
                    testId="client-filter-over-risk"
                />
                <FilterChip
                    label={`Allocation (${count('allocation')})`}
                    isSelected={filter === 'allocation'}
                    onClick={() => ctx.toggleClientFilter('allocation')}
                    testId="client-filter-allocation"
                />
                <FilterChip
                    label={`Any flag (${count('flagged')})`}
                    isSelected={filter === 'flagged'}
                    onClick={() => ctx.toggleClientFilter('flagged')}
                    testId="client-filter-flagged"
                />
            </>
        ),
    };
}

export default defineWebFeature({
    id: 'wealth.compliance.mandate-breaches',
    slots: {
        'desk.dashboard.tape': [
            {
                key: 'in-breach',
                order: 50,
                select: (_input, ctx): StatStripItem => {
                    const breachClientCount = flagsInView(ctx).size;
                    return {
                        key: 'in-breach',
                        label: 'In breach',
                        value: ctx.overview ? breachClientCount : ABSENT,
                        tone: breachClientCount > 0 ? 'warning' : 'neutral',
                        hint: 'Clients over their risk or outside the model. Press to show these clients only.',
                        active: ctx.clientFilter === 'flagged',
                        onClick: () => ctx.toggleClientFilter('flagged'),
                        testId: 'stat-in-breach',
                    };
                },
            },
        ],
        'desk.dashboard.clients.flags': [
            { key: 'breaches', order: 10, select: (_input, ctx) => breachFlagSource(ctx) },
        ],
    },
});
